/**
 * Notion category → projection group mapping, and the CSP bucket each group
 * rolls into.
 *
 * The mapping is NOT hand-written any more. Notion's CSP page already holds it:
 * the four bucket databases (Fixed Costs, Savings, Investments, Guilt Free
 * Spending) each contain rows whose `categories` relation lists exactly which
 * Notion categories roll into that line. Sync mirrors those rows into
 * `csp_groups` / `csp_group_categories`, and everything here reads from that.
 *
 * The old hardcoded `GROUP_OF` table was a snapshot of those same relations
 * taken by hand. It went stale the moment a category was added in Notion — a new
 * category fell through to a fallback group called "Unbucketed" that was pinned
 * to the `fixed` bucket, so an HSA contribution showed up as a fixed cost. That
 * whole failure mode is gone: an unmapped category now has no group at all and
 * is reported, not silently mis-bucketed.
 *
 * What stays local is the *tuning* Notion has no opinion about — does a line
 * drift with CPI, how far auto-balance may cut it, does it build an asset.
 */

import type {
  Category, CategoryItem, CspBucket, CspGroup, DeductionKind, DerivedCategory, Expense,
  ExpenseStatus, FundingSchedule, Recurrence, ScenarioItem, YnabCategoryActual, YnabCategoryLine,
  YnabGroupReconcile, YnabItemMappingRow, YnabMappingRow, YnabReachTarget,
  YnabReconcileItem,
} from './types.js';
import type { ProviderTarget } from './providers/types.js';
import { monthlyAmount } from './finance.js';
import {
  checkToAnnual, checkToGuaranteedMonth, checkToMonth, guaranteedMonth, indexSchedules,
  perCheck, scheduleFor,
} from './calendar.js';
import { normalizeForMatch } from './ynabMatch.js';

/** Strip dashes so Notion ids compare consistently. */
export const normalizeId = (id: string | null | undefined): string =>
  (id ?? '').replace(/-/g, '');

/**
 * What a line contributes when no dial has been set for it.
 *
 * 100% for an active item — it is in the plan, so it counts. 0% for a paused or
 * inactive one, so merely widening the item list with `includeInactive` never
 * switches one on by itself; only an explicit dial does.
 *
 * Exported because the *storage* layer needs the same answer. `putDials` prunes
 * dials that match the default rather than storing noise, and it used to assume
 * that default was always 100 — which silently threw away the one dial that
 * matters most on a paused row: the 100% that switches it on. One definition,
 * two readers, no drift.
 */
export const defaultDialFor = (status: ExpenseStatus): number => (status === 'A' ? 100 : 0);

/** The Guilt-Free line is a budget figure, not a rollup of expense rows. */
export const GFS_GROUP_KEY = 'gfs';
export const GFS_ITEM_ID = 'gfs-budget';

// ───────────────────────── group keys ─────────────────────────

export const slugify = (s: string): string =>
  s.toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * Historical group keys, kept alive on purpose.
 *
 * `scenario_dials`, `scenario_priority` and every saved scenario reference these
 * strings. Deriving keys straight from Notion row names would silently orphan
 * every dial the user has ever set, so the slug of a known row name maps back to
 * the key it has always had. Anything not listed here gets its slug, which is how
 * a brand-new CSP row joins without a code change.
 */
const LEGACY_KEY_BY_SLUG: Record<string, string> = {
  'rent-mortgage': 'housing',
  utilities: 'utilities',
  auto: 'auto',
  groceries: 'groceries',
  health: 'health',
  subscriptions: 'subs',
  miscellaneous: 'misc',
  'long-term-emergency-fund': 'emergency',
  'general-savings': 'savingsGoals',
  'post-tax-retirement-savings-401k': 'k401',
  'post-tax-retirement-savings-roth-ira': 'roth',
  stocks: 'brokerage',
  'guilt-free-spending': GFS_GROUP_KEY,
};

export const groupKeyForName = (name: string): string => {
  const slug = slugify(name);
  return LEGACY_KEY_BY_SLUG[slug] ?? slug;
};

// ───────────────────────── deduction kind ─────────────────────────

/**
 * Whether a CSP line is funded out of take-home, and if not, which side of tax
 * it sits on.
 *
 * Notion encodes the first half in the row's `income` relation: a row linked to
 * the Net income row is measured against take-home (its `incomePercentage`
 * formula resolves), and a row with no link is not (the formula comes back
 * empty). That is the flag — a payroll deduction never lands in the account the
 * budget is drawn from, so charging it against take-home double-counts it
 * against the tax that already removed it.
 *
 * Pre- vs post-tax comes from the row name, which is where it is written today
 * ("Pre-Tax Health Savings - HSA", "Post-Tax Retirement Savings - 401K"). If the
 * CSP databases ever gain an explicit select for this, read that instead — the
 * name prefix is the weakest link in this file.
 */
export function deductionKindFor(name: string, measuredAgainstIncome: boolean): DeductionKind {
  if (measuredAgainstIncome) return null;
  return /^\s*pre[-\s]?tax\b/i.test(name) ? 'pre' : 'post';
}

/** Does this group's money come out of take-home? */
export const fromTakeHome = (g: { deduction: DeductionKind }): boolean => g.deduction === null;

// ───────────────────────── local tuning ─────────────────────────

export interface GroupTuning {
  /** does this drift up with CPI? fixed-rate debt and contributions don't */
  inflates: boolean;
  /** auto-balance won't cut below this % of base */
  minPct: number;
  /** contributions that become assets rather than disappearing */
  asset?: boolean;
  /** approximate monthly principal paydown while funded */
  principal?: number;
}

/** Per-group overrides. Anything absent falls back to the bucket default. */
export const GROUP_TUNING: Record<string, GroupTuning> = {
  housing: { inflates: false, minPct: 100, principal: 400 },
  utilities: { inflates: true, minPct: 80 },
  auto: { inflates: true, minPct: 70 },
  groceries: { inflates: true, minPct: 60 },
  health: { inflates: true, minPct: 50 },
  subs: { inflates: true, minPct: 0 },
  misc: { inflates: true, minPct: 0 },
};

const BUCKET_TUNING: Record<CspBucket, GroupTuning> = {
  fixed: { inflates: true, minPct: 0 },
  invest: { inflates: false, minPct: 0, asset: true },
  savings: { inflates: false, minPct: 0, asset: true },
  gfs: { inflates: true, minPct: 0 },
};

export const tuningFor = (key: string, bucket: CspBucket): GroupTuning =>
  GROUP_TUNING[key] ?? BUCKET_TUNING[bucket];

// ───────────────────────── ordering ─────────────────────────

/** Buckets render in CSP order; groups inside a bucket keep their Notion order. */
const BUCKET_ORDER: CspBucket[] = ['fixed', 'invest', 'savings', 'gfs'];

export function orderGroups(groups: CspGroup[] = []): CspGroup[] {
  // Tolerates a missing list: /api/bootstrap gained `cspGroups` after the app
  // shipped, so a cached response from before the deploy has no field at all.
  return (groups ?? []).slice().sort((a, b) =>
    BUCKET_ORDER.indexOf(a.bucket) - BUCKET_ORDER.indexOf(b.bucket)
    || a.sortOrder - b.sortOrder
    || a.name.localeCompare(b.name));
}

/**
 * Default auto-balance cut order — most discretionary first, user-overridable.
 *
 * Derived from the group list rather than hardcoded so a new CSP row lands
 * somewhere sane instead of being absent from the cut order entirely (absent
 * meant auto-balance would never touch it). Guilt-Free first, then the rest of
 * the discretionary end, with housing last.
 */
const CUT_RANK: Record<string, number> = {
  gfs: 0, subs: 1, misc: 2, savingsGoals: 4, brokerage: 5,
  groceries: 6, health: 7, auto: 8, utilities: 9, roth: 10, k401: 11,
  emergency: 12, housing: 99,
};

export function cutPriorityFor(groups: CspGroup[]): string[] {
  return orderGroups(groups)
    .slice()
    .sort((a, b) => (CUT_RANK[a.key] ?? 3) - (CUT_RANK[b.key] ?? 3))
    .map(g => g.key);
}

/**
 * Cut order when the group list isn't to hand — a scenario read from D1 before
 * bootstrap has finished, mostly. Same ranking, just the keys we know of.
 */
export const DEFAULT_CUT_ORDER: string[] =
  Object.keys(CUT_RANK).sort((a, b) => CUT_RANK[a] - CUT_RANK[b]);

/** Ramit Sethi's CSP target bands. */
export const BUCKET_TARGETS: Record<CspBucket, { name: string; low: number; high: number }> = {
  fixed: { name: 'Fixed Costs', low: 50, high: 60 },
  invest: { name: 'Investments', low: 10, high: 10 },
  savings: { name: 'Savings', low: 5, high: 10 },
  gfs: { name: 'Guilt-Free', low: 20, high: 35 },
};

// ───────────────────────── lookup ─────────────────────────

export interface GroupIndex {
  byKey: Map<string, CspGroup>;
  byCategoryId: Map<string, CspGroup>;
  ordered: CspGroup[];
}

export function indexGroups(groups: CspGroup[] = []): GroupIndex {
  const ordered = orderGroups(groups);
  const byKey = new Map(ordered.map(g => [g.key, g]));
  const byCategoryId = new Map<string, CspGroup>();
  for (const g of ordered) {
    for (const cid of g.categoryIds) byCategoryId.set(normalizeId(cid), g);
  }
  return { byKey, byCategoryId, ordered };
}

export function groupForCategory(
  index: GroupIndex,
  categoryId: string | null | undefined,
): CspGroup | undefined {
  return index.byCategoryId.get(normalizeId(categoryId));
}

// ───────────────────────── derivation ─────────────────────────

/**
 * Roll active expenses up into dialable groups, each carrying its line items.
 * Rebuilt whenever expense data changes so a Notion sync stays in step.
 *
 * An expense whose category belongs to no CSP row is collected into
 * `unmapped` rather than being folded into a catch-all group. Silently bucketing
 * it is what produced the HSA-as-a-fixed-cost bug; surfacing it makes the gap in
 * Notion visible and keeps every total honest.
 */
export interface UnmappedExpense {
  expenseId: string;
  expenseName: string;
  categoryId: string | null;
  categoryName?: string;
}

export function deriveCategories(
  expenses: Expense[],
  recurrences: Recurrence[],
  gfsBudget: number,
  categories: Category[] = [],
  groups: CspGroup[] = [],
  opts: { includeInactive?: boolean; schedules?: FundingSchedule[] } = {},
): DerivedCategory[] {
  const index = indexGroups(groups);
  const recById = new Map(recurrences.map(r => [normalizeId(r.id), r]));
  const catById = new Map(categories.map(c => [normalizeId(c.id), c]));
  const map = new Map<string, DerivedCategory>();
  /*
   * Schedules arrive through `opts` rather than as a seventh positional
   * parameter so every existing call site keeps working untouched — and when
   * they're absent, `scheduleFor` returns undefined and `guaranteedMonth`
   * applies the 24 fallback, where the two axes coincide exactly. An un-synced
   * or schedule-less caller therefore gets `guaranteedBase === base`, which is
   * the honest answer rather than a silently-wrong 7.69% haircut.
   */
  const schedules = opts.schedules ?? [];
  const schedIndex = indexSchedules(schedules);

  for (const e of expenses) {
    const status = e.status ?? 'A';
    if (status !== 'A' && !opts.includeInactive) continue;

    const group = groupForCategory(index, e.categoryId);
    if (!group) continue; // reported by unmappedExpenses(), never silently bucketed
    const notionCat = catById.get(normalizeId(e.categoryId));

    if (!map.has(group.key)) {
      const t = tuningFor(group.key, group.bucket);
      map.set(group.key, {
        key: group.key,
        name: group.name,
        bucket: group.bucket,
        deduction: group.deduction,
        base: 0,
        guaranteedBase: 0,
        items: [],
        asset: t.asset ?? false,
        inflates: t.inflates,
        minPct: t.minPct,
        principal: t.principal ?? 0,
      });
    }

    const rec = recById.get(normalizeId(e.recurrenceId));
    const monthly = monthlyAmount(e, rec);
    // Guaranteed is derived from the *annual*, not from `saapRounded`. Notion
    // rounds each set-aside to whole dollars for YNAB's benefit; reconciling
    // against the rounded figure would import ±$25/check of display residue
    // into the projection's own arithmetic. Annual is the axis where all three
    // agree, so annual is what the conversion goes through.
    const sched = scheduleFor(schedIndex, e.fundingScheduleId, schedules);
    const guaranteed = guaranteedMonth(monthly * 12, sched);
    const cat = map.get(group.key)!;
    cat.base += monthly;
    cat.guaranteedBase += guaranteed;
    cat.items.push({
      id: e.id,
      name: e.name,
      base: monthly,
      guaranteedBase: guaranteed,
      paycheckCount: sched?.paycheckCount ?? 0,
      recurrenceName: rec?.name ?? '—',
      categoryName: notionCat?.name,
      categoryIcon: notionCat?.icon ?? null,
      status,
      defaultDialPct: defaultDialFor(status),
    });
  }

  // Guilt-Free is a budget line, not an expense row.
  const gfsGroup = index.byKey.get(GFS_GROUP_KEY);
  const gfsTuning = tuningFor(GFS_GROUP_KEY, 'gfs');
  map.set(GFS_GROUP_KEY, {
    key: GFS_GROUP_KEY,
    name: gfsGroup?.name ?? 'Guilt-Free Spending',
    bucket: 'gfs',
    deduction: gfsGroup?.deduction ?? null,
    base: gfsBudget,
    // Guilt-Free is already a monthly figure — a budget you spend within, not a
    // per-paycheck set-aside divided out of an annual cost. There is no
    // paycheck cadence to convert through, so both axes read the same number
    // and the guaranteed total is honest about that rather than inventing a
    // 7.69% haircut on money that was never scheduled.
    guaranteedBase: gfsBudget,
    items: [{
      id: GFS_ITEM_ID,
      name: 'Dining, movies, anything',
      base: gfsBudget,
      guaranteedBase: gfsBudget,
      paycheckCount: 0,
      recurrenceName: 'Monthly budget',
      status: 'A',
      defaultDialPct: 100,
    }],
    asset: false,
    inflates: gfsTuning.inflates,
    minPct: gfsTuning.minPct,
    principal: 0,
  });

  for (const c of map.values()) c.items.sort((a, b) => b.base - a.base);

  const order = index.ordered.map(g => g.key);
  const cats = order.filter(k => map.has(k)).map(k => map.get(k)!);
  // A group present in the data but missing from the ordered list would vanish
  // entirely; append rather than drop it.
  for (const [k, c] of map) if (!order.includes(k)) cats.push(c);

  return cats;
}

/**
 * Fold a scenario's what-if expenses into the derived categories.
 *
 * Returns a new list; the input is untouched, because the same `allCats` is
 * shared by every scenario in the comparison table and mutating it would leak
 * one scenario's hypotheticals into all of them.
 *
 * An item whose `groupKey` no longer matches any category is dropped rather
 * than bucketed somewhere plausible — the same rule `unmappedExpenses` follows,
 * and for the same reason: a line silently landing in the wrong bucket corrupts
 * a total in a way that is very hard to spot.
 *
 * The guaranteed base is derived through the sole active pay cadence, so a
 * hypothetical behaves like a real line on that cadence. With no cadence (or
 * two live ones, where there is no safe guess) `guaranteedMonth` applies the 24
 * fallback and both axes agree — honest rather than plausibly wrong.
 */
export function withScenarioItems(
  cats: DerivedCategory[],
  items: ScenarioItem[] = [],
  schedules: FundingSchedule[] = [],
): DerivedCategory[] {
  if (!items?.length) return cats;
  const sched = scheduleFor(indexSchedules(schedules), null, schedules);

  return cats.map(cat => {
    const mine = items.filter(i => i.groupKey === cat.key);
    if (!mine.length) return cat;

    const extra: CategoryItem[] = mine.map(i => ({
      id: i.id,
      name: i.name,
      base: i.monthly,
      guaranteedBase: guaranteedMonth(i.monthly * 12, sched),
      paycheckCount: sched?.paycheckCount ?? 0,
      recurrenceName: 'What-if',
      status: 'A',
      // 100, like any active line: you added it because you want to see it in
      // the plan. Adding a row that starts switched off would make the act of
      // adding it do nothing.
      defaultDialPct: 100,
    }));

    return {
      ...cat,
      base: cat.base + extra.reduce((s, i) => s + i.base, 0),
      guaranteedBase: cat.guaranteedBase + extra.reduce((s, i) => s + i.guaranteedBase, 0),
      items: [...cat.items, ...extra].sort((a, b) => b.base - a.base),
    };
  });
}

/** Is this id a scenario what-if rather than a Notion expense? */
export const isScenarioItemId = (id: string): boolean => id.startsWith('si-');

/**
 * Active expenses whose Notion category no CSP row claims.
 *
 * These are dropped from every total on purpose. The alternative — the old
 * `other` / "Unbucketed" group pinned to `fixed` — quietly inflated fixed costs
 * with whatever Notion had grown since the mapping was last hand-copied. A
 * visible list of "these aren't in your CSP yet" is the honest version, and the
 * fix is one relation in Notion rather than a code change.
 */
export function unmappedExpenses(
  expenses: Expense[],
  categories: Category[] = [],
  groups: CspGroup[] = [],
): UnmappedExpense[] {
  const index = indexGroups(groups);
  const catById = new Map(categories.map(c => [normalizeId(c.id), c]));
  const out: UnmappedExpense[] = [];

  for (const e of expenses) {
    if ((e.status ?? 'A') !== 'A') continue;
    if (groupForCategory(index, e.categoryId)) continue;
    out.push({
      expenseId: e.id,
      expenseName: e.name,
      categoryId: e.categoryId ?? null,
      categoryName: catById.get(normalizeId(e.categoryId))?.name,
    });
  }
  return out;
}

// ───────────────────────── the Miscellaneous rule ─────────────────────────
//
// Every other fixed-cost group is a rollup of real Notion rows: a mortgage, a
// phone bill, eight subscriptions. Miscellaneous is not. It is the allowance for
// the fixed costs that were never itemised — the thing that gets bought that no
// line predicted — and it is sized as a share of the fixed costs that *were*
// itemised.
//
// Now that Notion holds the complete category list, that allowance is computable
// rather than guessed: the itemised fixed costs dictate what Miscellaneous
// should be. Notion does not compute it, though. The Miscellaneous row is a
// hand-typed monthly amount, so every time the other fixed costs move, this
// number is silently a little wrong until someone goes and edits it.
//
// The base is the *itemised* fixed costs — everything except Miscellaneous —
// and the allowance is added on top of them. 15% of $4,521 of real bills is
// $678 of allowance; the total simply lands wherever it lands. Miscellaneous is
// deliberately not part of its own base: it is the padding on top of the
// itemised costs, not a slice carved out of a total it belongs to.
//
// Two ways to size it, because the right one changes with the month:
//
//   percent — scale with the bills. Fixed costs go up, the allowance goes up.
//   amount  — a flat set-aside per paycheck, when scaling proportionally isn't
//             affordable and the allowance needs to be pinned instead.

/** Default share of itemised fixed costs, as a percentage. */
export const MISC_DEFAULT_SHARE_PCT = 15;

/** The CSP group key Miscellaneous rolls into — see `LEGACY_KEY_BY_SLUG`. */
export const MISC_GROUP_KEY = 'misc';

/**
 * How far Miscellaneous may sit from the rule before it's worth re-typing.
 *
 * Deliberately wider than `DRIFT_TOLERANCE`. That one guards a comparison
 * between two systems holding the same figure, where a dollar apart means
 * someone forgot to copy an edit. This one guards a derived target on a ~$500
 * line: a subscription renewing at a new price moves it by cents, and a cue that
 * fires on cents is a cue that gets ignored.
 */
export const MISC_DRIFT_TOLERANCE = 5;

/** How the Miscellaneous allowance is sized. */
export type MiscRuleMode = 'percent' | 'amount';

/**
 * The Miscellaneous rule, as configured.
 *
 * `prompt` is separate from the sizing on purpose. "What should this line be"
 * and "tell me when it isn't" are different questions, and there are months
 * where the honest answer to the second is no — the rule stays visible as the
 * reference it is, without a standing instruction to go and edit Notion.
 */
export interface MiscRule {
  mode: MiscRuleMode;
  /** percent of the itemised fixed costs, when `mode` is 'percent' */
  sharePct: number;
  /** flat set-aside per paycheck, when `mode` is 'amount' */
  perPay: number;
  /** raise the update-Notion cue when the line drifts off the rule */
  prompt: boolean;
}

export const DEFAULT_MISC_RULE: MiscRule = {
  mode: 'percent',
  sharePct: MISC_DEFAULT_SHARE_PCT,
  perPay: 0,
  prompt: true,
};

/** `app_settings` keys the rule persists under. Free-form table, no migration. */
export const MISC_RULE_KEYS = {
  mode: 'misc_rule_mode',
  sharePct: 'misc_rule_share_pct',
  perPay: 'misc_rule_per_pay',
  prompt: 'misc_rule_prompt',
} as const;

/**
 * Read the rule out of `app_settings`, falling back to the default per field.
 *
 * Per field rather than all-or-nothing: someone who has only ever changed the
 * percentage should not lose it the day a new key joins the set.
 */
export function readMiscRule(settings: Record<string, string> = {}): MiscRule {
  const num = (raw: string | undefined, fallback: number): number => {
    const v = Number(raw);
    return Number.isFinite(v) && v >= 0 ? v : fallback;
  };
  const mode = settings[MISC_RULE_KEYS.mode];
  return {
    mode: mode === 'amount' ? 'amount' : 'percent',
    sharePct: num(settings[MISC_RULE_KEYS.sharePct], DEFAULT_MISC_RULE.sharePct),
    perPay: num(settings[MISC_RULE_KEYS.perPay], DEFAULT_MISC_RULE.perPay),
    // Absent means "not configured yet", and the useful default is to be told.
    prompt: settings[MISC_RULE_KEYS.prompt] !== '0',
  };
}

/** Miscellaneous measured against the rule that's supposed to size it. */
export interface MiscBudget {
  /** the rule this was measured against */
  rule: MiscRule;
  /** the Notion expense ids that make up Miscellaneous — active lines only */
  expenseIds: string[];
  /** what Notion currently has, per month */
  actual: number;
  /** the same figure per paycheck — what the reconcile table's left column holds */
  actualPerPay: number;
  /** the itemised fixed costs the allowance sits on top of, per month */
  otherFixed: number;
  /** fixed costs as they stand today, Miscellaneous included */
  fixedTotal: number;
  /** what the rule says Miscellaneous should be, per month */
  target: number;
  /** the same target per paycheck — what to set aside */
  targetPerPay: number;
  /** fixed costs once Miscellaneous is `target` */
  targetFixedTotal: number;
  /** target − actual: positive means Notion is under-budgeting */
  drift: number;
  /** what Miscellaneous works out to today as a share of the itemised costs */
  actualSharePct: number;
  /** the figure doesn't match the rule — a fact, regardless of `prompt` */
  offRule: boolean;
  /** off the rule *and* the rule is set to say so — the cue fires */
  prompt: boolean;
}

/**
 * Size Miscellaneous from the fixed costs it sits on top of.
 *
 * `percent` mode is `sharePct × otherFixed` — a straight share of the itemised
 * bills, with Miscellaneous itself excluded from the base. (Including it would
 * make the rule self-referential: the allowance would appear on both sides, and
 * a Miscellaneous that had drifted low would drag down the very total it is
 * measured against and under-report its own correction.)
 *
 * `amount` mode ignores the fixed costs entirely and pins the allowance to a
 * flat per-paycheck set-aside — the escape hatch for a month where scaling with
 * the bills isn't affordable.
 *
 * Returns null when there's no Miscellaneous group or no itemised fixed costs
 * to sit on top of — a bootstrap that hasn't loaded, mostly.
 */
export function miscBudget(
  cats: DerivedCategory[] = [],
  rule: MiscRule = DEFAULT_MISC_RULE,
  /**
   * The cadence Miscellaneous is funded on.
   *
   * This used to be `paychecksPerMonth` — `floor(N/12)`, i.e. 2 — used as a
   * unit conversion in both directions. On the 26-cadence Miscellaneous
   * actually sits on, that reported a $511.35 line as a $255.68 set-aside
   * (its true SAAP is $236.01) and a $250 flat rule as a $500 monthly target
   * (it is $541.67). Both were 7.69% out, in opposite directions, which is
   * why neither looked obviously wrong.
   */
  schedule?: Pick<FundingSchedule, 'paycheckCount'> | null,
): MiscBudget | null {
  const misc = (cats ?? []).find(c => c.key === MISC_GROUP_KEY);
  if (!misc) return null;

  const fixedTotal = cats.filter(c => c.bucket === 'fixed').reduce((s, c) => s + c.base, 0);
  const otherFixed = fixedTotal - misc.base;
  if (otherFixed <= 0) return null;

  // `amount` mode pins a flat per-paycheck set-aside, so its monthly
  // equivalent is the set-aside times the real conversion factor — never
  // times the guaranteed floor, which would under-state it by two paychecks
  // a year on a bi-weekly cadence.
  const target = rule.mode === 'amount'
    ? checkToMonth(rule.perPay, schedule)
    : (rule.sharePct / 100) * otherFixed;
  const drift = target - misc.base;
  const offRule = Math.abs(drift) >= MISC_DRIFT_TOLERANCE;

  return {
    rule,
    expenseIds: misc.items.filter(i => i.status === 'A').map(i => i.id),
    actual: misc.base,
    actualPerPay: perCheck(misc.base * 12, schedule),
    otherFixed,
    fixedTotal,
    target,
    targetPerPay: perCheck(target * 12, schedule),
    targetFixedTotal: otherFixed + target,
    drift,
    actualSharePct: (misc.base / otherFixed) * 100,
    offRule,
    prompt: offRule && rule.prompt,
  };
}

/**
 * Is this the reconciliation row Miscellaneous lives on?
 *
 * Matched on expense ids rather than group name on purpose. The reconcile table
 * is keyed by Notion's `computedGroup` ("Other"), while the CSP row that owns
 * the 15% rule is called "Miscellaneous" — two different names for the two ends
 * of the same money, and renaming either in Notion would silently unhook the
 * cue. The expense ids survive both renames.
 */
export function fundsMiscellaneous(
  g: Pick<YnabGroupReconcile, 'items' | 'unmatched' | 'notTrackedInYnab'>,
  expenseIds: Set<string>,
): boolean {
  if (!expenseIds.size) return false;
  return [...g.items, ...g.unmatched, ...g.notTrackedInYnab]
    .some(i => expenseIds.has(i.expenseId));
}

// ───────────────────────── YNAB reconciliation ─────────────────────────

/*
 * `paychecksPerMonth()` used to live here.
 *
 * It returned `min(floor(paycheckCount / 12))` across the active schedules and
 * was used for two incompatible jobs: deciding what a YNAB target can be, and
 * converting a per-check figure into a monthly one. The first is a feasibility
 * question whose answer really is the floor. The second is arithmetic whose
 * answer is `N/12`, and the two coincide only when N is 24. Every 26-cadence
 * line was therefore 7.69% short — two paychecks a year, silently discarded.
 *
 * `min` across schedules made it worse: with a 24 and a 26 cadence both live
 * by design, one scalar cannot be right for both baskets, and the smaller one
 * was applied to everything.
 *
 * Both jobs now live in `shared/calendar.ts` as separate functions
 * (`guaranteedChecksPerMonth` and `monthlyFactor`), and conversion happens per
 * item. Deleted rather than deprecated: a reachable one-size scalar is an
 * invitation to reintroduce the bug.
 */

/**
 * YNAB goal kinds that state a *monthly* amount, and are therefore comparable
 * to a set-aside on the guaranteed axis.
 *
 * `TB` (target balance) and `TBD` (target balance by date) are deliberately
 * absent: a $15,000 Rolex fund is not a monthly figure, and adding it to a $400
 * monthly Parents transfer produces a number that means nothing. Those go down
 * the `reachTargets` path instead.
 */
/**
 * Below a dollar the difference is per-item rounding, not drift worth flagging.
 * Notion rounds each SAAP to whole dollars, so a group of eight lines can land
 * a few dollars off a target that is, in every meaningful sense, correct.
 */
export const DRIFT_TOLERANCE = 1;

/**
 * Which axis a category reconciles on.
 *
 * These used to inspect YNAB's `goal_type` directly. They now read the
 * canonical `target.kind` the adapter produced, so a second provider with a
 * different goal vocabulary needs no change here — see
 * `shared/providers/types.ts` and `worker/providers/ynab.ts`.
 */
export const isMonthlyTarget = (cat: { target: ProviderTarget }): boolean =>
  cat.target.kind === 'monthly';

export const isReachTarget = (cat: { target: ProviderTarget }): boolean =>
  cat.target.kind === 'reach';

/**
 * What the provider wants in this category *this month* — its "Amount to
 * Assign".
 *
 * This, not `budgeted`, is the right-hand side of the reconciliation.
 * `budgeted` is whatever happens to have been assigned so far, which drifts
 * through the month and makes a correct budget look wrong: Groceries reads
 * 342.29 assigned against a 268 target, reporting a 74-dollar gap where Notion
 * and YNAB actually agree exactly.
 *
 * The cadence arithmetic that used to live here is now the adapter's business
 * (`worker/providers/ynab.ts`) — this just reads the answer.
 */
export function amountToAssign(cat: { target: ProviderTarget }): number {
  return cat.target.monthlyAmount;
}

/**
 * YNAB category groups that can never correspond to a Notion expense.
 *
 * Credit-card payment categories are the mirror of spending already counted in
 * the category it was spent from — mapping one would double-count it. Offering
 * them as choices only makes the real list harder to search.
 */
const UNMAPPABLE_GROUPS = new Set(['Credit Card Payments']);

/**
 * Ready to Assign — the holding pen income lands in before it's given a job.
 *
 * Excluded by name rather than by YNAB's `internal` flag, which is too blunt:
 * it also covers `Uncategorized`, and that one is a perfectly real place to
 * park spending you haven't classified yet. Both sit in "Internal Master
 * Category", so the group name can't separate them either.
 */
const isReadyToAssign = (name: string): boolean =>
  /^\s*inflow\s*:/i.test(name) || /ready to assign/i.test(name);

/** Can this YNAB category meaningfully point at a Notion expense? */
export const isMappableYnabCategory = (cat: {
  name: string;
  categoryGroupName: string;
}): boolean => !isReadyToAssign(cat.name) && !UNMAPPABLE_GROUPS.has(cat.categoryGroupName);

/**
 * Every Notion expense, shaped for the reconciliation and keyed by id.
 *
 * Each item carries its own funding schedule, because a schedule is a property
 * of the *line*, not of the basket it happens to be summed into. Housing is
 * funded on the 26-cadence and HSA on the 24, and one global `× 2` applied
 * across both understated the 26-lines by 7.69% while leaving the 24-lines
 * looking perfect — which is exactly why the discrepancy was so hard to see.
 */
export function toReconcileItems(
  expenses: Expense[],
  recurrences: Recurrence[],
  categories: Category[],
  schedules: FundingSchedule[] = [],
): Map<string, YnabReconcileItem & { computedGroup: string | null }> {
  const recById = new Map(recurrences.map(r => [normalizeId(r.id), r]));
  const catById = new Map(categories.map(c => [normalizeId(c.id), c]));
  const schedIndex = indexSchedules(schedules);
  const out = new Map<string, YnabReconcileItem & { computedGroup: string | null }>();

  for (const e of expenses) {
    const cat = catById.get(normalizeId(e.categoryId));
    const sched = scheduleFor(schedIndex, e.fundingScheduleId, schedules);
    // `SAAP Rounded` — the amount genuinely moved into YNAB each paycheck.
    // Reconciling against monthlyAmount() instead reports per-item rounding
    // (Health: 131.34 against a real 128) as though it were drift between the
    // two systems, which buries the drift that is real.
    const setAside = Number(e.saapRounded) || 0;
    out.set(e.id, {
      expenseId: e.id,
      name: e.name,
      status: e.status ?? 'A',
      setAside,
      monthly: monthlyAmount(e, recById.get(normalizeId(e.recurrenceId))),
      fundingScheduleId: e.fundingScheduleId ?? null,
      fundingScheduleName: sched?.name ?? null,
      paycheckCount: sched?.paycheckCount ?? 0,
      // The two monthly readings of the same set-aside. They differ only when
      // the cadence isn't 24, and the difference is the extra paychecks.
      setAsideMonthly: checkToMonth(setAside, sched),
      setAsideGuaranteedMonthly: checkToGuaranteedMonth(setAside, sched),
      setAsideAnnual: checkToAnnual(setAside, sched),
      budgetTarget: e.budgetTarget ?? null,
      categoryName: cat?.name,
      computedGroup: cat?.computedGroup ?? null,
      ynabCategoryId: null,
      ynabCategoryName: null,
    });
  }
  return out;
}

/** Active first, then biggest set-aside — an expanded row leads with what it funds. */
export const byFundingWeight = (a: YnabReconcileItem, b: YnabReconcileItem): number =>
  (a.status === 'A' ? 0 : 1) - (b.status === 'A' ? 0 : 1)
  || b.setAside - a.setAside
  || a.name.localeCompare(b.name);

/**
 * Which Notion expenses each YNAB category funds, from the saved line-item
 * mapping.
 */
export function expensesByYnabCategory(
  itemMapping: YnabItemMappingRow[],
): Map<string, string[]> {
  return new Map(itemMapping.map(m => [m.ynabCategoryId, m.expenseIds]));
}

/**
 * Auto-suggest which Notion expense a YNAB category funds.
 *
 * Same normalisation as the group-level matcher, but one-to-one and on the
 * *expense* name rather than the group: YNAB's `3️⃣ Parents` against Notion's
 * `Parents`. Only exact normalised-name equality counts — a near-miss silently
 * mapped to the wrong line would corrupt a total in a way that's very hard to
 * spot, and an unmapped row is loudly visible by design.
 */
export function suggestExpensesForYnabCategory(
  ynabCategoryName: string,
  expenses: Expense[],
): string[] {
  const want = normalizeLineName(ynabCategoryName);
  if (!want) return [];
  return expenses.filter(e => normalizeLineName(e.name) === want).map(e => e.id);
}

/**
 * `normalizeForMatch` without the `| Suffix` truncation.
 *
 * The group-level matcher drops everything after a pipe on purpose — `Housing |
 * Mortgage` and `Housing | DC Rent` are both the Housing group. One level down
 * that rule is actively wrong: `3️⃣ Emergency Fund | Cash` is a *different* YNAB
 * line from `3️⃣ Emergency Fund`, and truncating would map both to the same
 * Notion expense and count it twice.
 */
function normalizeLineName(raw: string): string {
  return normalizeForMatch(raw.replace(/\|/g, ' '));
}

/**
 * A first pass at the line-item mapping, for a human to correct.
 *
 * Two rules, in order:
 *
 * 1. **Exact name.** `3️⃣ Parents` funds Notion's `Parents`. High confidence and
 *    group-independent.
 * 2. **Sole category.** If a Notion group maps to exactly one YNAB category,
 *    that category funds everything in the group. `1️⃣ 🩺 Health` is one line
 *    covering Protein, Carbo Gain, Platinum Multivitamins and Olympus Gym —
 *    none of which is called "Health", so rule 1 alone leaves the whole group
 *    unmatched and the row reading zero.
 *
 * A group mapped to *several* YNAB categories gets no fallback. Housing splits
 * across Mortgage, DC Rent and DC Fixed Costs, and which Notion line belongs to
 * which is a judgement only the user can make — guessing would put real money
 * against the wrong line, where it looks correct and is not.
 */
export function seedItemMapping(
  mapping: YnabMappingRow[],
  ynabCategories: YnabCategoryActual[],
  expenses: Expense[],
  categories: Category[],
): YnabItemMappingRow[] {
  const catById = new Map(categories.map(c => [normalizeId(c.id), c]));
  const groupOf = (e: Expense) => catById.get(normalizeId(e.categoryId))?.computedGroup ?? null;

  const byCategory = new Map<string, Set<string>>();
  const add = (ynabCategoryId: string, expenseId: string) => {
    const set = byCategory.get(ynabCategoryId) ?? new Set<string>();
    set.add(expenseId);
    byCategory.set(ynabCategoryId, set);
  };

  // Rule 1 — exact name, anywhere. Credit-card payment and internal categories
  // are excluded outright: they mirror spending already counted elsewhere, so a
  // mapping would double-count it.
  const mappable = ynabCategories.filter(isMappableYnabCategory);
  const claimed = new Set<string>();
  for (const cat of mappable) {
    for (const id of suggestExpensesForYnabCategory(cat.name, expenses)) {
      add(cat.id, id);
      claimed.add(id);
    }
  }

  // Rule 2 — sole mapped category takes the rest of its group.
  for (const m of mapping) {
    if (m.ynabCategoryIds.length !== 1) continue;
    const only = m.ynabCategoryIds[0];
    if (!mappable.some(c => c.id === only)) continue;
    for (const e of expenses) {
      if (claimed.has(e.id)) continue;
      if (groupOf(e) !== m.notionGroup) continue;
      add(only, e.id);
    }
  }

  return [...byCategory].map(([ynabCategoryId, ids]) => ({
    ynabCategoryId, expenseIds: [...ids], ignored: false,
  }));
}

/**
 * Build the reconciliation rows: one per mapped Notion computedGroup, with both
 * sides scoped to the same basket.
 *
 * The two mapping levels compose. Level one says which YNAB categories belong to
 * a Notion group; level two says which Notion expense each of those categories
 * funds. A group's Notion figure is therefore the sum over
 * `group → its YNAB categories → their mapped expenses` — never over the whole
 * computedGroup, which is a wider set and produced the Savings row reading 2,420
 * against YNAB's 1,454 (the group carries 401K, Roth IRA, HSA and Robinhood; the
 * five mapped YNAB categories know about none of them).
 *
 * Expenses in the computedGroup that no YNAB category claims are returned in
 * `unmatched` and left out of every total. Excluding them silently would restore
 * the same class of bug in reverse, so they are counted and shown.
 */
export function buildReconcileGroups(
  mapping: YnabMappingRow[],
  itemMapping: YnabItemMappingRow[],
  ynabCategories: YnabCategoryActual[],
  expenses: Expense[],
  recurrences: Recurrence[],
  categories: Category[],
  /**
   * The live cadences. Conversion is per item from here on — a group holding
   * both a 24-line and a 26-line has no single correct factor, and applying
   * one anyway is the bug this whole change exists to remove.
   */
  schedules: FundingSchedule[],
  /** Notion expense ids with no YNAB counterpart by design — 401K, HSA, … */
  notTrackedExpenseIds: Set<string> = new Set(),
): YnabGroupReconcile[] {
  const items = toReconcileItems(expenses, recurrences, categories, schedules);
  const ynabById = new Map(ynabCategories.map(c => [c.id, c]));
  const expenseIdsFor = expensesByYnabCategory(itemMapping);

  /** Sum a per-item monthly field over the active lines only. */
  const sumActive = (
    list: YnabReconcileItem[],
    pick: (i: YnabReconcileItem) => number,
  ): number => list.filter(i => i.status === 'A').reduce((s, i) => s + pick(i), 0);

  const ignoredIds = new Set(itemMapping.filter(m => m.ignored).map(m => m.ynabCategoryId));

  return mapping.map(m => {
    const all = m.ynabCategoryIds.map(id => ynabById.get(id)).filter((c): c is YnabCategoryActual => !!c);

    // A category the user has declared they don't track in Notion can never
    // have a Notion counterpart, so leaving it in the YNAB total would create a
    // gap that no amount of mapping could ever close. Listed rather than
    // dropped, so the exclusion stays visible.
    const ignoredCategories = all.filter(c => ignoredIds.has(c.id)).map(c => ({
      ynabCategoryId: c.id, ynabCategoryName: c.name, target: amountToAssign(c),
    }));
    const cats = all.filter(c => !ignoredIds.has(c.id));

    // A balance goal is funded opportunistically rather than to a monthly
    // set-aside, so its assigned figure would read as drift on a monthly
    // comparison. Split the categories before claiming anything.
    const reachCats = cats.filter(isReachTarget);
    const monthlyCats = cats.filter(c => !isReachTarget(c));

    // ── the Notion side, reached through the item mapping ──
    //
    // Monthly categories claim first. An expense mapped to both a monthly and a
    // balance category belongs on the monthly axis — that's where it's actually
    // funded — and claiming in mapping order instead would make the group total
    // depend on the order the checkboxes happened to be ticked in.
    const claimed = new Map<string, YnabReconcileItem>();
    const claimFor = (cat: YnabCategoryActual): YnabReconcileItem[] => {
      const mine: YnabReconcileItem[] = [];
      for (const expenseId of expenseIdsFor.get(cat.id) ?? []) {
        const item = items.get(expenseId);
        if (!item) continue; // expense deleted in Notion; mapping row is stale
        const tagged = { ...item, ynabCategoryId: cat.id, ynabCategoryName: cat.name };
        mine.push(tagged);
        if (!claimed.has(expenseId)) claimed.set(expenseId, tagged);
      }
      return mine.sort(byFundingWeight);
    };

    // One line per monthly YNAB category. A target belongs to a category, not
    // to a group — Housing's three categories were set in three different
    // months, and a single group-level "target set" date can only be wrong
    // about two of them.
    const lines: YnabCategoryLine[] = monthlyCats.map(cat => {
      const mine = claimFor(cat);
      const setAside = sumActive(mine, i => i.setAside);
      // Summed per item rather than scaled as a basket: each line converts on
      // its own cadence, so a category funding both a 24-line and a 26-line
      // lands on the sum of two correct parts instead of one wrong whole.
      const monthly = sumActive(mine, i => i.setAsideGuaranteedMonthly);
      const accrualMonthly = sumActive(mine, i => i.setAsideMonthly);
      const target = amountToAssign(cat);
      return {
        ynabCategoryId: cat.id,
        ynabCategoryName: cat.name,
        ynabCategoryGroupName: cat.categoryGroupName,
        goalType: cat.target.nativeKind,
        targetKind: cat.target.kind,
        targetWritable: cat.target.writable,
        budgeted: cat.budgeted,
        target,
        targetSetOn: cat.target.setOn,
        setAside,
        // `monthly` stays the guaranteed axis, because that is what a YNAB
        // target is: a figure you can hit in any month, including the ten that
        // hold only two paychecks. Comparing it to the accrual monthly would
        // report the two extra paychecks a year as drift.
        monthly,
        accrualMonthly,
        annual: sumActive(mine, i => i.setAsideAnnual),
        // Only meaningful once there's a target to be stale *against* and a
        // Notion figure to compare it to.
        stale: isMonthlyTarget(cat)
          && !!cat.target.setOn
          && setAside > 0
          && Math.abs(monthly - target) >= DRIFT_TOLERANCE,
        items: mine,
      };
    });

    const monthlyItems = lines.flatMap(l => l.items).sort(byFundingWeight);

    const reachTargets: YnabReachTarget[] = reachCats.map(cat => {
      const notionItems = claimFor(cat);
      return {
        ynabCategoryId: cat.id,
        ynabCategoryName: cat.name,
        goalType: cat.target.nativeKind ?? 'TB',
        goalTarget: cat.target.overallAmount,
        funded: cat.target.funded,
        left: cat.target.left,
        percentComplete: cat.target.percentComplete ?? 0,
        targetMonth: cat.target.targetMonth,
        budgetedThisMonth: cat.budgeted,
        notionItems,
        notionSetAside: sumActive(notionItems, i => i.setAside),
        notionMonthly: sumActive(notionItems, i => i.setAsideGuaranteedMonthly),
      };
    });

    const notionSetAside = sumActive(monthlyItems, i => i.setAside);

    // ── what's in the group but nothing claims ──
    //
    // Split in two: lines still waiting for a mapping, and lines that will
    // never have one because they don't exist in YNAB at all. Both stay out of
    // the totals; only the first is a problem.
    const orphans = [...items.values()]
      .filter(i => i.computedGroup === m.notionGroup && !claimed.has(i.expenseId))
      .sort(byFundingWeight);
    const notTrackedInYnab = orphans.filter(i => notTrackedExpenseIds.has(i.expenseId));
    const unmatched = orphans.filter(i => !notTrackedExpenseIds.has(i.expenseId));

    return {
      notionGroup: m.notionGroup,
      ynabCategoryIds: m.ynabCategoryIds,
      notionSetAside,
      // The YNAB-comparable figure — guaranteed axis, summed per item.
      notionMonthly: sumActive(monthlyItems, i => i.setAsideGuaranteedMonthly),
      // The same set-asides on the accrual axis. This is what should agree
      // with `notionMonthlyRaw`; a gap between the two is per-item rounding
      // in Notion's SAAP, and a gap against `notionMonthly` is the extra
      // paychecks. Two different stories, so two different numbers.
      notionSetAsideMonthly: sumActive(monthlyItems, i => i.setAsideMonthly),
      notionAnnual: sumActive(monthlyItems, i => i.setAsideAnnual),
      notionMonthlyRaw: sumActive(monthlyItems, i => i.monthly),
      inactiveSetAside: monthlyItems.filter(i => i.status !== 'A').reduce((s, i) => s + i.setAside, 0),
      ynabBudgeted: monthlyCats.reduce((s, c) => s + c.budgeted, 0),
      ynabTarget: lines.reduce((s, l) => s + l.target, 0),
      ignoredCategories,
      hasStaleTarget: lines.some(l => l.stale),
      /** Distinct cadences funding this group — >1 means no single factor exists. */
      scheduleIds: [...new Set(
        monthlyItems.filter(i => i.status === 'A' && i.fundingScheduleId)
          .map(i => i.fundingScheduleId as string),
      )],
      lines,
      items: monthlyItems,
      unmatched,
      unmatchedSetAside: sumActive(unmatched, i => i.setAside),
      unmatchedMonthly: sumActive(unmatched, i => i.setAsideGuaranteedMonthly),
      notTrackedInYnab,
      notTrackedSetAside: sumActive(notTrackedInYnab, i => i.setAside),
      notTrackedMonthly: sumActive(notTrackedInYnab, i => i.setAsideGuaranteedMonthly),
      reachTargets,
    };
  });
}
